import { Box, Button, Col, Row, Text } from '@opendoor/bricks-next';
import { EntryComponent } from 'cms/entries/entries';
import { Desc, Subhead } from 'components/landing-pages-v2/shared/Typography';
import { ILpComponentImageCard } from 'declarations/contentful';

import Grid, { gridStyles } from './Grid';

const RenderImageCard = (entry: ILpComponentImageCard) => {
  const { fields } = entry;
  const desktopImageSourceUrl = fields?.desktopImage;
  const mobileImageSourceUrl = fields?.mobileImage;
  const mobileDescriptionDisplay = fields?.hideDescriptionOnMobile ? 'none' : 'block'; // By default, we hide description on mobile
  const isSmallPadding = fields?.verticalPadding === 'Small';

  return (
    <Col analyticsName={`cosmos-landing-page-image-card-${fields?.analyticsId}`} trackImpression>
      <Grid backgroundColor="$backgroundSecondary">
        <Box
          $platform-web={{ gridColumn: gridStyles.colSpan12 }}
          flexDirection="column"
          width="100%"
          height="$500x"
          my={isSmallPadding ? '$10x' : '$68x'}
          $largerThanXS={{
            height: '$616x',
            my: isSmallPadding ? '$16x' : '$24x',
          }}
          $largerThanSM={{
            height: '$452x',
            flexDirection: 'row',
            my: isSmallPadding ? '$24x' : '$68x',
          }}
          $largerThanMD={{ height: '$719x' }}
          borderRadius="$12x"
          overflow="hidden"
          backgroundColor="$backgroundPrimary"
        >
          <Row overflow="hidden" $largerThanSM={{ flex: 1, flexBasis: 0, flexShrink: 1 }}>
            <Col
              p="$16x"
              flexShrink={1}
              $largerThanSM={{ p: '$20x' }}
              $largerThanMD={{ p: '$32x' }}
              justifyContent="flex-start"
            >
              {fields?.subtitle && (
                <Row flexGrow={1} pb="$6x" $largerThanSM={{ pb: '$0x' }}>
                  <Subhead>{fields.subtitle}</Subhead>
                </Row>
              )}
              <Col>
                <Col
                  width="100%"
                  maxWidth="$200x"
                  minHeight="$75x"
                  $largerThanXS={{ maxWidth: '$250x', minHeight: '$93x' }}
                  $largerThanSM={{ maxWidth: '$272x', minHeight: '$105x' }}
                  $largerThanMD={{ maxWidth: '$430x', minHeight: '$168x' }}
                >
                  <Text
                    tag="h2"
                    typography="$headerSmall"
                    display="block"
                    mb={'$8x'}
                    color="$contentPrimary"
                    $largerThanMD={{ typography: '$headerLarge' }}
                  >
                    {fields.title}
                    {fields.secondaryTitle && (
                      <Text
                        tag="span"
                        typography="$headerSmall"
                        color="$contentTertiary"
                        $largerThanMD={{ typography: '$headerLarge' }}
                      >
                        {' '}
                        {fields.secondaryTitle}
                      </Text>
                    )}
                  </Text>
                </Col>
                <Desc
                  display={mobileDescriptionDisplay}
                  $largerThanSM={{ display: 'block' }}
                  $largerThanMD={{ fontSize: 18 }}
                >
                  {fields.description}
                </Desc>
                {fields?.ctaLink && fields?.ctaDisplayText && (
                  <Button
                    variant="primary"
                    aria-label={`Go to page to ${fields?.ctaDisplayText}`}
                    analyticsName={`cosmos-landing-page-image-card-cta-${fields?.analyticsId}`}
                    onPress={() => fields?.ctaLink && window.location.assign(fields?.ctaLink)}
                    mt="$12x"
                    $largerThanSM={{
                      mt: '$16x',
                    }}
                    $largerThanMD={{
                      mt: '$24x',
                    }}
                    size="small"
                    width="$180x"
                    minHeight="$48x"
                    label={fields?.ctaDisplayText}
                  />
                )}
              </Col>
            </Col>
          </Row>
          <Box
            flex={1}
            flexBasis={0}
            flexShrink={1}
            $largerThanSM={{ p: '$8x' }}
            $largerThanMD={{ p: '$10x' }}
          >
            <Box
              $platform-web={{ display: 'grid' }}
              overflow="hidden"
              width="100%"
              height="100%"
              $largerThanSM={{ borderRadius: '$8x' }}
            >
              <picture style={{ display: 'contents' }}>
                <source media="(min-width: 768px)" srcSet={desktopImageSourceUrl} />
                <img
                  src={mobileImageSourceUrl}
                  alt="illustration of a home"
                  loading="lazy"
                  decoding="async"
                  style={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: '80%',
                  }}
                />
              </picture>
            </Box>
          </Box>
        </Box>
      </Grid>
    </Col>
  );
};

const ImageCard: EntryComponent<ILpComponentImageCard> = {
  render: RenderImageCard,
};

export default ImageCard;
